import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';

import { useLogout, useSession } from '@/features/auth/useSession';
import { featuresOf, isManager, type Features } from '@/shared/session/currentUser';
import {
  FileTextIcon,
  FolderIcon,
  InboxIcon,
  InfoIcon,
  LogoutIcon,
  MoreIcon,
  ToggleIcon,
  UserIcon,
  UsersIcon,
  XIcon,
} from '@/shared/ui/icons';
import { Logo } from '@/shared/ui/Logo';
import { ScreenErrorBoundary } from '@/shared/ui/ScreenErrorBoundary';
import { cn } from '@/shared/utils/cn';

/**
 * The map of the app.
 *
 * Desktop: a 224 px sidebar with the sections (icon and name; the one-line hint moved to
 * `title`, so a short row does not need a subtitle), «Estadísticas» and «Administración»
 * for an administrator, and at the bottom who is logged in and «Salir».
 *
 * Phone (`< lg`): a thin bar with the logo and the name of the current screen, and a
 * fixed bottom bar with the sections (three or four, whether Documentos is switched on;
 * see `navItemsFor`) and «Más», which opens a sheet from the bottom with the rest
 * (Estadísticas and Administración for an administrator, Perfil, Acerca de, Salir). No
 * «Menú» button hiding the whole navigation: the sections of the day are one tap away,
 * always visible.
 *
 * The order is the order of the day: the mail comes in, becomes quotes, for customers;
 * the archive, the numbers and the switches are for whoever runs the thing.
 */
interface NavItem {
  to: string;
  label: string;
  hint: string;
  icon: ReactNode;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/correos', label: 'Bandeja', hint: 'Lo que ha llegado y qué es', icon: <InboxIcon /> },
  {
    to: '/presupuestos',
    label: 'Presupuestos',
    hint: 'De la oferta al PDF',
    icon: <FileTextIcon />,
  },
  { to: '/clientes', label: 'Clientes', hint: 'A quién se presupuesta', icon: <UsersIcon /> },
  {
    to: '/documentos',
    label: 'Documentos',
    hint: 'Facturas, albaranes y lo que se guarda',
    icon: <FolderIcon />,
  },
];

/**
 * `NAV_ITEMS`, minus Documentos when the archive is off (fase 1, ADR 0016 del back): la
 * pantalla no es alcanzable, así que no tiene sentido enseñar el enlace.
 */
function navItemsFor(features: Features): NavItem[] {
  return features.document_archive
    ? NAV_ITEMS
    : NAV_ITEMS.filter((item) => item.to !== '/documentos');
}

/**
 * Shown only to an administrator. The API refuses them to anybody else anyway.
 * Estadísticas vive aquí desde el contrato v10.9: es la revisión semanal de Ignira, no
 * una pantalla de Cubas (`GET /api/stats/triage` es `admin`-only).
 */
/** The management half (contract v10.11): Ignira, or the `is_admin` switch. */
const ADMIN_NAV_ITEMS: NavItem[] = [
  {
    to: '/administracion',
    label: 'Administración',
    hint: 'Usuarios, buzones y qué hace por su cuenta',
    icon: <ToggleIcon />,
  },
];

/** Reachable from the account block on desktop and from «Más» on a phone. */
const ACCOUNT_NAV_ITEMS: NavItem[] = [
  { to: '/perfil', label: 'Perfil', hint: 'Tu cuenta y tu contraseña', icon: <UserIcon /> },
  { to: '/acerca-de', label: 'Acerca de', hint: 'Qué hace la IA y qué no', icon: <InfoIcon /> },
];

/** Screens without an entry of their own, so the phone header still names them. */
const OTHER_SCREENS: Array<{ to: string; label: string }> = [
  { to: '/activacion', label: 'Activación' },
];

/** Tailwind needs the literal class name in the source to keep it: a computed
 * `grid-cols-${n}` string would be purged. Keyed by columns including «Más». */
const BOTTOM_BAR_GRID_COLS: Record<number, string> = {
  4: 'grid-cols-4',
  5: 'grid-cols-5',
};

function isUnder(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

export function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const logout = useLogout();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const sheetCloseRef = useRef<HTMLButtonElement>(null);

  // Estadísticas moved into Administración on 2026-10-02: the platform half of the
  // sidebar is gone, and its tabs are behind «Administración».
  const adminItems = isManager(session.data) ? ADMIN_NAV_ITEMS : [];
  const navItems = navItemsFor(featuresOf(session.data));
  const sidebarItems = [...navItems, ...adminItems];
  const bottomItems = navItems.slice(0, 4);
  const moreItems = [...navItems.slice(4), ...adminItems, ...ACCOUNT_NAV_ITEMS];
  const currentScreen = [...sidebarItems, ...ACCOUNT_NAV_ITEMS, ...OTHER_SCREENS].find((item) =>
    isUnder(location.pathname, item.to),
  );
  const moreIsCurrent = moreItems.some((item) => isUnder(location.pathname, item.to));

  // The sheet closes with Escape and gives the focus back to «Más»; while open, the
  // focus starts on its close button so a keyboard reader is inside it, not behind it.
  useEffect(() => {
    if (!moreOpen) return;
    sheetCloseRef.current?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setMoreOpen(false);
        moreButtonRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [moreOpen]);

  function closeMore() {
    setMoreOpen(false);
  }

  function handleLogout() {
    closeMore();
    logout.mutate(undefined, {
      onSettled: () => {
        void navigate('/entrar', { replace: true });
      },
    });
  }

  const displayName = session.data?.full_name?.trim() || session.data?.email;
  // What the person is, under their name (contract v10.13): «Superadministrador»,
  // «Administrador», or nothing. What they see is decided by their mailboxes.
  const roleLabel = session.data
    ? session.data.is_superadmin
      ? 'Superadministrador'
      : session.data.is_admin
        ? 'Administrador'
        : undefined
    : undefined;

  const sidebarNav = (
    <nav aria-label="Principal" className="flex flex-col gap-0.5">
      {sidebarItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          title={item.hint}
          className={({ isActive }) =>
            cn(
              'group flex h-9 items-center gap-2.5 rounded-md px-3 text-[14px] font-medium transition-colors',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/70',
              isActive
                ? 'bg-white/10 text-white'
                : 'text-nav-text hover:bg-white/5 hover:text-white',
            )
          }
        >
          {({ isActive }) => (
            <>
              <span
                className={cn(
                  'shrink-0',
                  isActive ? 'text-brand' : 'text-nav-text group-hover:text-white',
                )}
              >
                {item.icon}
              </span>
              <span className="truncate">{item.label}</span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );

  const sidebarAccount = (
    <div className="flex flex-col gap-2.5">
      {session.data ? (
        <div className="min-w-0">
          <NavLink
            to="/perfil"
            className="block truncate text-[13.5px] font-medium text-white underline-offset-2 hover:underline"
            title={session.data.email}
          >
            {displayName}
          </NavLink>
          <p className="text-nav-text/80 text-[12px]">{roleLabel}</p>
        </div>
      ) : null}
      <div className="flex items-center gap-2">
        <NavLink
          to="/acerca-de"
          className="text-nav-text/80 text-[12px] underline-offset-2 hover:text-white hover:underline"
        >
          Acerca de
        </NavLink>
        <button
          type="button"
          onClick={handleLogout}
          disabled={logout.isPending}
          className="text-nav-text ml-auto inline-flex h-8 items-center justify-center gap-1.5 rounded-md bg-white/5 px-2.5 text-[13px] font-medium hover:bg-white/10 hover:text-white disabled:opacity-50"
        >
          <LogoutIcon size={16} />
          Salir
        </button>
      </div>
    </div>
  );

  return (
    <div className="bg-paper min-h-screen lg:grid lg:grid-cols-[224px_1fr]">
      {/* Phone: a thin bar with the logo and where you are. */}
      <header className="bg-nav flex h-12 items-center justify-between gap-3 px-4 text-white lg:hidden">
        <Logo inverted className="h-7" />
        {currentScreen ? (
          <p className="truncate text-[14px] font-medium text-white/90">{currentScreen.label}</p>
        ) : null}
      </header>

      {/* Desktop: the sidebar. */}
      <aside className="bg-nav hidden text-white lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col">
        <div className="px-5 pt-6 pb-4">
          <Logo inverted className="h-14 self-start" />
        </div>
        <div className="flex flex-1 flex-col px-3 py-1">{sidebarNav}</div>
        <div className="border-t border-white/10 px-4 py-4">{sidebarAccount}</div>
      </aside>

      <main className="mx-auto flex w-full max-w-[1400px] flex-col gap-5 px-4 py-5 pb-24 sm:px-6 lg:px-10 lg:py-6 lg:pb-6">
        {/* Keyed by section, not by full path: switching tabs inside Administración must
            not remount the page, but leaving a crashed screen for another one must clear it. */}
        <ScreenErrorBoundary key={location.pathname.split('/')[1] ?? ''}>
          <Outlet />
        </ScreenErrorBoundary>
      </main>

      {/* Phone: the bottom bar. */}
      <nav
        aria-label="Secciones"
        className="bg-card border-line fixed inset-x-0 bottom-0 z-30 border-t pb-[env(safe-area-inset-bottom)] lg:hidden"
      >
        <ul className={cn('grid', BOTTOM_BAR_GRID_COLS[bottomItems.length + 1] ?? 'grid-cols-5')}>
          {bottomItems.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                onClick={closeMore}
                className={({ isActive }) =>
                  cn(
                    'flex h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-medium',
                    'focus-visible:outline-accent focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2',
                    isActive ? 'text-accent' : 'text-ink-3',
                  )
                }
              >
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            </li>
          ))}
          <li>
            <button
              ref={moreButtonRef}
              type="button"
              aria-expanded={moreOpen}
              aria-controls="hoja-mas"
              aria-haspopup="dialog"
              onClick={() => setMoreOpen((value) => !value)}
              className={cn(
                'flex h-14 w-full flex-col items-center justify-center gap-0.5 text-[11px] font-medium',
                'focus-visible:outline-accent focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2',
                moreOpen || moreIsCurrent ? 'text-accent' : 'text-ink-3',
              )}
            >
              <MoreIcon />
              <span>Más</span>
            </button>
          </li>
        </ul>
      </nav>

      {/* Phone: the sheet behind «Más». */}
      {moreOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div aria-hidden="true" onClick={closeMore} className="bg-ink/40 absolute inset-0" />
          <div
            id="hoja-mas"
            role="dialog"
            aria-modal="true"
            aria-label="Más"
            className="bg-card absolute inset-x-0 bottom-0 flex flex-col rounded-t-2xl pb-[env(safe-area-inset-bottom)] shadow-lg"
          >
            <div className="border-line-2 flex items-center justify-between gap-3 border-b px-5 py-3">
              {session.data ? (
                <div className="min-w-0">
                  <p className="text-ink truncate text-[14.5px] font-medium">{displayName}</p>
                  <p className="text-ink-3 text-[12.5px]">{roleLabel}</p>
                </div>
              ) : (
                <p className="text-ink text-[14.5px] font-medium">Más</p>
              )}
              <button
                ref={sheetCloseRef}
                type="button"
                aria-label="Cerrar"
                onClick={closeMore}
                className="text-ink-3 hover:bg-paper-2 hover:text-ink inline-flex size-9 shrink-0 items-center justify-center rounded-md"
              >
                <XIcon size={18} />
              </button>
            </div>
            <nav aria-label="Más" className="flex flex-col p-2">
              {moreItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={closeMore}
                  className={({ isActive }) =>
                    cn(
                      'flex h-11 items-center gap-3 rounded-md px-3 text-[14.5px] font-medium',
                      isActive ? 'bg-paper-2 text-ink' : 'text-ink-2 hover:bg-paper-2',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className={cn('shrink-0', isActive ? 'text-accent' : 'text-ink-3')}>
                        {item.icon}
                      </span>
                      {item.label}
                    </>
                  )}
                </NavLink>
              ))}
              <button
                type="button"
                onClick={handleLogout}
                disabled={logout.isPending}
                className="text-ink-2 hover:bg-paper-2 flex h-11 items-center gap-3 rounded-md px-3 text-left text-[14.5px] font-medium disabled:opacity-50"
              >
                <span className="text-ink-3 shrink-0">
                  <LogoutIcon />
                </span>
                Salir
              </button>
            </nav>
          </div>
        </div>
      ) : null}
    </div>
  );
}
